import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut as firebaseSignOut, 
  sendPasswordResetEmail, 
  updatePassword as firebaseUpdatePassword,
  EmailAuthProvider, 
  reauthenticateWithCredential as firebaseReauthenticateWithCredential,
} from "firebase/auth";
import { getApp as getFirebaseApp, getApps as getFirebaseApps, initializeApp as initializeFirebaseApp } from "firebase/app";
import { login as trackLogin, sign_up as trackSignup } from "../services/gtag";

// Note: We don't strictly need to initialize here if next-firebase-auth does it,
// but for client-side direct usage (like below), getting the auth instance is needed.
// next-firebase-auth initializes the default app.

const getAuthInstance = () => {
  // If next-firebase-auth has initialized, getAuth() should work with the default app.
  // However, we need to ensure an app is initialized.
  // Typically next-firebase-auth handles this in _app.js or similar via initAuth.
  // We can safely assume getAuth() works if initAuth has run.
  
  // Fallback if needed (though usually initAuth handles it)
  if (getFirebaseApps().length === 0) {
     const firebaseConfig = {
      apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
      authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
      databaseURL: process.env.NEXT_PUBLIC_FIREBASE_DATABASE_URL,
      projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
      storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGE_SENDER_ID,
      appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
    };
    initializeFirebaseApp(firebaseConfig);
  }
  
  return getAuth();
};

export const setFirebaseLanguage = (lang: string) => {
  let firebaseLang = lang;
  if (lang === "zh") firebaseLang = "zh-Hant";
  else if (lang === "cn") firebaseLang = "zh-Hans";
  
  const auth = getAuthInstance();
  auth.languageCode = firebaseLang;
};

export function signIn(username: string, password: string) {
  trackLogin("email");
  const auth = getAuthInstance();
  return signInWithEmailAndPassword(auth, username, password);
}

export function signUp(username: string, password: string) {
  trackSignup("email");
  const auth = getAuthInstance();
  return createUserWithEmailAndPassword(auth, username, password);
}

export function signOut() {
  const auth = getAuthInstance();
  return firebaseSignOut(auth);
}

export function resetPassword(email: string) {
  const auth = getAuthInstance();
  return sendPasswordResetEmail(auth, email);
}

export function updatePassword(password: string) {
  const auth = getAuthInstance();
  if (auth.currentUser) {
    return firebaseUpdatePassword(auth.currentUser, password);
  }
  return Promise.reject(new Error("No user logged in"));
}

export function reauthenticateWithCredential(password: string) {
  const auth = getAuthInstance();
  const user = auth.currentUser;
  if (!user || !user.email) return Promise.reject(new Error("No user logged in"));
  
  const credential = EmailAuthProvider.credential(
    user.email,
    password
  );
  return firebaseReauthenticateWithCredential(user, credential);
}

// Exporting things that might be used elsewhere
export { EmailAuthProvider };
